// src/services/modeloRendererService.ts
//
// Motor que transforma um DocumentoModelo (JSON do editor visual fabric.js,
// com placeholders {{TOKEN}}) em HTML pronto para ser rasterizado pelo mesmo
// pipeline html2canvas + jsPDF já usado em propostaPdfGenerator/osPdfGenerator.
//
// Antes desta peça, o "Gerenciador de Modelos" só editava e salvava o
// canvas_json — nada no sistema o lia de volta para gerar um documento real.

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { DocumentoModelo, TipoDocumentoModelo } from '../types/documentoModelo';
import { modeloDocumentoService } from './modeloDocumentoService';

interface FabricObjectLike {
  type?: string;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
  angle?: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  rx?: number;
  ry?: number;
  text?: string;
  fontSize?: number;
  fontWeight?: string | number;
  fontStyle?: string;
  fontFamily?: string;
  textAlign?: string;
  underline?: boolean;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  src?: string;
  opacity?: number;
}

/** Item de uma tabela dinâmica ({{TABELA_ITENS}}). */
export interface ItemTabelaModelo {
  descricao: string;
  quantidade: string;
  valorUnitario: string;
  valorTotal: string;
}

const TOKEN_TABELA_ITENS = 'TABELA_ITENS';

/**
 * Substitui {{TOKEN}} pelos dados reais.
 * IMPORTANTE: token sem valor vira string vazia — nunca deixamos o token cru
 * (ex: "{{PESO_KG}}") aparecer no PDF que vai para o cliente.
 */
function substituirTokens(texto: string, dados: Record<string, string>): string {
  return texto.replace(/\{\{\s*([A-Z0-9_]+)\s*\}\}/g, (_match, chave) => {
    const valor = dados[chave];
    return valor !== undefined && valor !== null ? String(valor) : '';
  });
}

/** Renderiza a tabela dinâmica de itens na área ocupada pelo campo {{TABELA_ITENS}}. */
function renderTabelaItens(obj: FabricObjectLike, itens: ItemTabelaModelo[]): string {
  const left = obj.left ?? 0;
  const top = obj.top ?? 0;
  const width = (obj.width ?? 694) * (obj.scaleX ?? 1) || 694;
  const fontSize = (obj.fontSize ?? 11) * (obj.scaleY ?? 1);
  const cor = obj.fill ?? '#334155';
  const fonte = obj.fontFamily || 'Arial';

  if (!itens.length) return '';

  const linhas = itens
    .map(
      (item, idx) => `
      <tr style="border-bottom:1px solid #e2e8f0; background-color:${idx % 2 === 0 ? '#ffffff' : '#f8fafc'};">
        <td style="width:58%; padding:6px 4px; text-align:left;">${escapeHtml(item.descricao)}</td>
        <td style="width:18%; padding:6px 4px; text-align:center;">${escapeHtml(item.quantidade)}</td>
        <td style="width:24%; padding:6px 4px; text-align:right; font-weight:bold; color:#0f172a;">${escapeHtml(item.valorTotal)}</td>
      </tr>`
    )
    .join('');

  return `<table style="position:absolute; left:${left}px; top:${top}px; width:${width}px; border-collapse:collapse; font-size:${fontSize}px; font-family:${fonte}, sans-serif; color:${cor};"><tbody>${linhas}</tbody></table>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function renderObjeto(
  obj: FabricObjectLike,
  dados: Record<string, string>,
  itens: ItemTabelaModelo[]
): string {
  const scaleX = obj.scaleX ?? 1;
  const scaleY = obj.scaleY ?? 1;
  const angle = obj.angle ?? 0;
  const transform = angle ? ` transform: rotate(${angle}deg); transform-origin: top left;` : '';

  if (obj.type === 'rect') {
    const w = (obj.width ?? 0) * scaleX;
    const h = (obj.height ?? 0) * scaleY;
    const borderRadius = obj.rx || obj.ry ? `border-radius: ${obj.rx ?? obj.ry}px;` : '';
    const border = obj.stroke ? `border: ${obj.strokeWidth ?? 1}px solid ${obj.stroke};` : '';
    return `<div style="position:absolute; left:${obj.left ?? 0}px; top:${obj.top ?? 0}px; width:${w}px; height:${h}px; background-color:${obj.fill ?? 'transparent'}; ${border} ${borderRadius} opacity:${obj.opacity ?? 1};${transform}"></div>`;
  }

  if (obj.type === 'circle') {
    const r = (obj as any).radius ?? 0;
    const size = r * 2 * scaleX;
    const border = obj.stroke ? `border: ${obj.strokeWidth ?? 1}px solid ${obj.stroke};` : '';
    return `<div style="position:absolute; left:${obj.left ?? 0}px; top:${obj.top ?? 0}px; width:${size}px; height:${size}px; background-color:${obj.fill ?? 'transparent'}; ${border} border-radius:50%; opacity:${obj.opacity ?? 1};${transform}"></div>`;
  }

  if (obj.type === 'line') {
    const x1 = obj.x1 ?? 0;
    const y1 = obj.y1 ?? 0;
    const x2 = obj.x2 ?? 0;
    const y2 = obj.y2 ?? 0;
    const length = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    const lineAngle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
    const thickness = obj.strokeWidth ?? 1;
    return `<div style="position:absolute; left:${x1}px; top:${y1 - thickness / 2}px; width:${length}px; height:${thickness}px; background-color:${obj.stroke ?? '#000'}; transform: rotate(${lineAngle}deg); transform-origin: top left;"></div>`;
  }

  if (obj.type === 'image') {
    const w = (obj.width ?? 0) * scaleX;
    const h = (obj.height ?? 0) * scaleY;
    const src = substituirTokens(obj.src || '', dados);
    // Se o token não foi resolvido (ex: sem assinatura cadastrada), não renderiza um ícone de imagem quebrada.
    if (!src || /\{\{[A-Z0-9_]+\}\}/.test(src)) return '';
    return `<img src="${src}" referrerPolicy="no-referrer" style="position:absolute; left:${obj.left ?? 0}px; top:${obj.top ?? 0}px; width:${w}px; height:${h}px; object-fit:contain; opacity:${obj.opacity ?? 1};${transform}" />`;
  }

  if (obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox') {
    const rawText = obj.text ?? '';

    // Bloco dinâmico: o campo marca a área onde a tabela de itens deve ser desenhada.
    if (rawText.includes(`{{${TOKEN_TABELA_ITENS}}}`)) {
      return renderTabelaItens(obj, itens);
    }

    const textoFinal = substituirTokens(rawText, dados);
    const linhasHtml = textoFinal.split('\n').map((linha) => escapeHtml(linha)).join('<br/>');
    const weight = obj.fontWeight || 'normal';
    const style = obj.fontStyle && obj.fontStyle !== 'normal' ? 'italic' : 'normal';
    const decoration = obj.underline ? 'underline' : 'none';
    const w = obj.width ? `width:${obj.width * scaleX}px;` : 'width:max-content; max-width:700px;';
    return `<div style="position:absolute; left:${obj.left ?? 0}px; top:${obj.top ?? 0}px; ${w} font-size:${(obj.fontSize ?? 14) * scaleY}px; font-family:${obj.fontFamily || 'Arial'}, sans-serif; font-weight:${weight}; font-style:${style}; text-decoration:${decoration}; color:${obj.fill ?? '#000'}; text-align:${obj.textAlign || 'left'}; white-space:pre-wrap; line-height:1.35;${transform}">${linhasHtml}</div>`;
  }

  return '';
}

/**
 * Extrai a lista de páginas (cada uma como array de objetos fabric) de um
 * canvas_json, suportando tanto o formato multi-página atual
 * ({version, pages: [jsonString, ...]}) quanto o formato legado de página única
 * ({version, objects: [...]})
 */
function extrairPaginas(canvasJson: string): FabricObjectLike[][] {
  let parsed: any;
  try {
    parsed = typeof canvasJson === 'string' ? JSON.parse(canvasJson) : canvasJson;
  } catch {
    return [[]];
  }

  if (Array.isArray(parsed?.pages) && parsed.pages.length > 0) {
    return parsed.pages.map((p: any) => {
      try {
        const pageParsed = typeof p === 'string' ? JSON.parse(p) : p;
        return Array.isArray(pageParsed?.objects) ? pageParsed.objects : [];
      } catch {
        return [];
      }
    });
  }

  return [Array.isArray(parsed?.objects) ? parsed.objects : []];
}

/**
 * Renderiza um modelo salvo no editor visual, com os tokens {{TOKEN}} já
 * substituídos pelos dados reais, como uma lista de HTML — um item por
 * página — prontos para serem colocados num container 794x1123 e passados
 * para html2canvas + jsPDF (mesmo pipeline já usado nos geradores atuais).
 */
export function renderModeloParaPaginasHtml(
  modelo: DocumentoModelo,
  dados: Record<string, string>,
  itens: ItemTabelaModelo[] = []
): string[] {
  const paginas = extrairPaginas(modelo.canvas_json);
  return paginas.map((objetos) => objetos.map((obj) => renderObjeto(obj, dados, itens)).join('\n'));
}

/**
 * Escolhe qual modelo será usado para gerar o documento:
 * 1º) o marcado como "padrão" do tipo; 2º) o ativo editado mais recentemente.
 * (Antes a regra era só "o mais recente vence", o que tornava imprevisível
 * qual modelo sairia quando havia mais de um ativo.)
 */
export async function buscarModeloAtivo(tipo: TipoDocumentoModelo): Promise<DocumentoModelo | null> {
  try {
    const modelos = await modeloDocumentoService.getModelos();
    const ativosDoTipo = modelos.filter((m) => m.tipo === tipo && m.ativo);

    const marcadoComoPadrao = ativosDoTipo.find((m) => m.padrao);
    if (marcadoComoPadrao) return marcadoComoPadrao;

    return (
      ativosDoTipo.sort(
        (a, b) => new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime()
      )[0] || null
    );
  } catch (e) {
    console.warn('Erro ao buscar modelo de documento ativo, usando layout padrão embutido:', e);
    return null;
  }
}

/** Gera um PDF completo a partir de um modelo do editor visual + dados, reaproveitando html2canvas + jsPDF. */
export async function gerarPdfDeModelo(
  modelo: DocumentoModelo,
  dados: Record<string, string>,
  nomeArquivo: string,
  itens: ItemTabelaModelo[] = []
): Promise<void> {
  const paginasHtml = renderModeloParaPaginasHtml(modelo, dados, itens);

  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  document.body.appendChild(container);

  try {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    for (let i = 0; i < paginasHtml.length; i++) {
      const pageDiv = document.createElement('div');
      pageDiv.style.width = `${modelo.largura_px || 794}px`;
      pageDiv.style.height = `${modelo.altura_px || 1123}px`;
      pageDiv.style.backgroundColor = '#ffffff';
      pageDiv.style.position = 'relative';
      pageDiv.style.overflow = 'hidden';
      pageDiv.innerHTML = paginasHtml[i];
      container.innerHTML = '';
      container.appendChild(pageDiv);

      const canvas = await html2canvas(pageDiv, {
        scale: 2.2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true,
      });

      const imgData = canvas.toDataURL('image/png');
      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297, undefined, 'FAST');
    }

    pdf.save(nomeArquivo);
  } finally {
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
  }
}
